import { BCMSCurrency, BCMSRecord, BCMSStateManagerCRUD, BCMSValidate, Branding, BrandingStyle, DisabledPrompt, Link, useBCMSContext, useModel, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import DateSelect from 'components/custom-ui/DateSelect';
import UIRadioGroup, { UIRadio } from 'components/custom-ui/UIRadioGroup';
import { ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView, WidgetContainer } from 'components/shared';
import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";
import StopPaymentHistoryWidget from "components/shared/widgets/StopPaymentHistoryWidgets/StopPaymentHistoryWidget";
import { PrimaryButton, UIAmountInput, UICheckbox, UITextInput } from 'components/ui';
import StopPayment from "components/workflows/StopPaymentsWorkflow/classes/StopPayment";
import Classes, { Account, CheckingAccount, LOCAccount, StopPayment as StopPaymentType } from "model";
import React from "react";
import "./styles.css";
import "./mobile.css";


/**
 * @function StopPaymentPage
 * @return {React.Component}
 * @exported true
 * @description The main component for StopPaymentPage
 */
export default function StopPaymentPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);
    const { branding } = useModel();
    const { options } = branding;
    const data = { ...getDefaultPageData(options, globalController), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    const { nextStep, state: workflow } = useWorkflow() ?? {};
    const getFilteredStopPayments = accountID => StopPaymentType.initAll().filter(item => item.account.id === accountID);

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "payment", prop ], value)
            .submit(setState);
        },

        // START: functions for the stop payment history widget.
        onStopPaymentWidgetHistoryChange: prop => val => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "stopPaymentHistoryWidget", prop ], val)
            .submit(setState);
        },

        onStopPaymentWidgetHistoryClick: evt => {
            BCMSStateManagerCRUD.init(state)
                .update([ "ui", "stopPaymentHistoryWidget", "showPayments" ], true)
                .update([ "data", "stopPaymentHistoryWidget", "payments" ], getFilteredStopPayments(state.data.stopPaymentHistoryWidget.account.id))
                .submit(setState);
        },
        // END: functions for the stop payment history widget.

        onCustomFieldChange: (fieldID, fieldType) => value => {
          var updatePaymentProp = undefined;
          var updatePaymentPropValue = undefined;

          const fields = state.data.payment[fieldType].map(field => {

            if(field.id === fieldID) {
              const fieldType = field.fieldType;

              if(fieldType.toLowerCase().includes("check amount")) {
                updatePaymentProp = "amount";
                updatePaymentPropValue = value;
              } else if(fieldType.toLowerCase().includes("check number")) {
                updatePaymentProp = "checkNumber";
                updatePaymentPropValue = value;
              }

              field.enteredValue = value;

            }
            return field;
          });

          BCMSStateManagerCRUD.init(state)
          .update([ "data", "payment", updatePaymentProp ], updatePaymentPropValue)
          .update([ "data", "payment", fieldType ], fields)
          .submit(setState);
      },

        onSubmit: evt => {
            const payment = state.data.payment;
            payment.setDefaults(globalController.state.branding);

            const data = BCMSStateManagerCRUD.init(workflow)
                .update([ "data", "payment" ], payment)
                .update([ "data", "stopPaymentHistoryWidget" ], state.data.stopPaymentHistoryWidget)
                .update([ "ui" ], state.ui)
                .submit();
            window.scrollTo(0, 0);
            nextStep(data);
        }
    });

    return (
        <PageContainer className="stop-payment-page" state={ { data, ui } } controller={ controller }>
            <StopPaymentContent />
            
        </PageContainer>
    );
}

export const getDefaultPageData = (brandingOptions, controller, stopPaymentHistoryWidgetProps) => {
  let options = brandingOptions ?? {};
  let stopPaymentFeeAmount = options.stop_payment_fee_amount_abi22;
  stopPaymentFeeAmount = stopPaymentFeeAmount === "undefined" ? "X.XX" : stopPaymentFeeAmount;
  stopPaymentFeeAmount = BCMSValidate.isNumber(stopPaymentFeeAmount)
  ? BCMSCurrency.init(stopPaymentFeeAmount).format()
  : stopPaymentFeeAmount;
  let showConfirmationCheckbox = options.stoppayment_checkbox_abi22;
  showConfirmationCheckbox = showConfirmationCheckbox === "true";

  const stopCheckCustomFields = StopPayment.getArrayOfCustomFieldObjectsByBrandingOptionType(controller.state.branding, `stop_check_field`);
  const stopRangeCustomFields = StopPayment.getArrayOfCustomFieldObjectsByBrandingOptionType(controller.state.branding, `stop_range_field`);
  const payment = StopPayment.init({ stopCheckCustomFields, stopRangeCustomFields, hasStopPaymentFee: showConfirmationCheckbox });
  const hasRangeOfChecks = options.range_of_checks_abi22 !== "false";

  // START: stop payment history widget data.
  const account = stopPaymentHistoryWidgetProps?.accountID ? Account.initByType(stopPaymentHistoryWidgetProps?.accountID) : Account.getDefaultBrandedAccountByType("checking");
  let _accounts = Account.hasLOCAccounts() ? LOCAccount.initAll() : CheckingAccount.initAll();
  _accounts = stopPaymentHistoryWidgetProps?.accounts ?? _accounts;
  let _payments = StopPaymentType.initAll().filter(item => item.account.id === account.id);
  _payments = stopPaymentHistoryWidgetProps?.payments ?? _payments;
  const _allStopPayments = stopPaymentHistoryWidgetProps?.allStopPayments ?? StopPaymentType.initAll();
  // END: stop payment history widget data.

  return {
    hasRangeOfChecks,
    payment: payment,
    stopPaymentFeeAmount: stopPaymentFeeAmount,
    stopPaymentHistoryWidget: {  
        account, 
        accounts: _accounts, 
        payments: _payments, 
        allStopPayments: _allStopPayments 
    }
  }
};
export const getDefaultPageUI = () => ({
  accountDropdown: { isActive: false },
  reasonDropdown: { isActive: false },
  stopPaymentHistoryWidget: {
    showPayments: false,
    accountSelect: { isActive: false }
  }
});

function StopPaymentContent(props) {
    const { data, ui, onChange, onCustomFieldChange, onSubmit, 
    onStopPaymentWidgetHistoryChange, onStopPaymentWidgetHistoryClick } = useStateManager();

    const stopPayment = data.payment;
    const { branding } = useModel();
    const { options } = branding;
    const isSinglePayment = stopPayment.paymentType === "Single Check";
    const isRangePayment = stopPayment.paymentType === "Range of Checks";
    const feeMessage = `A fee of ${ data.stopPaymentFeeAmount } applies for stop payment.`;

    // START: Build an array of custom fields set through branding options.
    const singleCheckFields = getFieldsByType("stopCheckCustomFields", stopPayment, onCustomFieldChange, ui);
    const rangeOfChecksFields = getFieldsByType("stopRangeCustomFields", stopPayment, onCustomFieldChange, ui);
    // END: Build an array of custom fields set through branding options.

    const subHeaderContent = options.stop_payments_subheader_abi22;
    const promoText = options.stop_payment_promo_abi22;
    const subfooterText = options.stoppayment_subfooter_abi22;

    const enabledSubmitButton = () => {
      if(stopPayment.hasStopPaymentFee && !stopPayment.agreeToStopPaymentFee) return false;
      return true;
    };

    return (
        <PageContentView>
          <div className="flex-container flex-vertical page-widget-wrapper">
            {promoText && <CustomContentHeaderWidget content = {promoText}/>}
            <Branding id="stop_payment_fee_abi22" value="true" default="false">
              <BrandingStyle style={{ "color" : "stop_payment_fee_text_abi22" }} className="stop-payment-fee" element={ <p>{ feeMessage }</p> } />          
            </Branding>

            <WidgetContainer className="stop-payment-widget">
              <ContentHeaderView text="Stop Payments" data-point="Stop_Payment"/>
                {subHeaderContent && <CustomContentHeaderWidget content = {subHeaderContent}/>}
                <Branding id="stoppayment_history_widget_abi22" value="false" default="false">
                  <Link className="stop-payment-history-link" to="/stopPaymentHistory" dataPoint="Stop_Payment_History">Stop Payment History</Link>
                </Branding>
                <FormContainer className="small-form-width">
                  <FormRowView text="Stop Payment Account:">
                    {/* AccountSelect */}
                    <AccountSelect dataPoint="Account_DD"  onChange={ onChange("account") } value={stopPayment.account} />
                  </FormRowView>
                  <FormRowView text="">
                    {/* UIRadios */}
                    <UIRadioGroup label="Stop Payment Type:" value={stopPayment.paymentType} onChange={onChange("paymentType")} >
                      <UIRadio name="stop-payment-type" label="Single Check" dataPoint="Single_Check" />
                      { data.hasRangeOfChecks && <UIRadio name="stop-payment-type" label="Range of Checks" dataPoint="Range_Check"  /> }
                    </UIRadioGroup>
                  </FormRowView>
                  { isSinglePayment && <> 
                  { singleCheckFields }
                  </>}
                  { isRangePayment && <>
                    <FormRowView text="Starting Check Number:">
                      <UITextInput onChange={onChange("startCheckNumber")} value={stopPayment.startCheckNumber} data-point="Start_Check_Number_Input" />
                    </FormRowView>
                    <FormRowView text="Ending Check Number:">
                      <UITextInput onChange={onChange("endCheckNumber")} value={stopPayment.endCheckNumber} ata-point="End_Check_Number_Input"/>
                    </FormRowView>
                    { rangeOfChecksFields }
                  </>}
                  { stopPayment.hasStopPaymentFee && 
                  <FormRowView className="checkbox-form-row distribute-payments-row">
                    <UICheckbox label={ `I understand a ${data.stopPaymentFeeAmount } stop payment fee will be assessed.`} 
                    value={ stopPayment.agreeToStopPaymentFee ? "checked" : "" } 
                    onChange={ onChange("agreeToStopPaymentFee")} />
                  </FormRowView>
                  }
                  
                  <FormRowView>
                  { enabledSubmitButton() &&
                    <PrimaryButton text="Submit" data-point="Submit" onClick={ onSubmit } />
                  }
                  { !enabledSubmitButton() &&
                    <span>
                      <DisabledPrompt>
                        <PrimaryButton className="disabled-button" text="Submit" data-point="Submit" onClick={ onSubmit } />
                      </DisabledPrompt>
                    </span>
                  }
                  </FormRowView>
                </FormContainer>
              </WidgetContainer>
              <Branding id="stoppayment_history_widget_abi22" value="true" default="false">
                <StopPaymentHistoryWidget data={ data.stopPaymentHistoryWidget } ui={ ui.stopPaymentHistoryWidget }
                onChange={ onStopPaymentWidgetHistoryChange } onClick={ onStopPaymentWidgetHistoryClick } />
              </Branding>
              { subfooterText && <CustomContentHeaderWidget content={ subfooterText } /> }
            </div>
          </PageContentView>
    )
}

export function AccountSelect(props){
    const { ui } = useStateManager() ?? {};
    const isActive = ui !== undefined && ui.accountDropdown ? ui.accountDropdown.isActive : false;
    const accounts = Account.hasLOCAccounts() ? LOCAccount.initAll() : CheckingAccount.initAll();
    const value = props.value === undefined || props.value === "" ? accounts[0] : props.value;

    let accountItems = accounts.map(function(account){
        return  <CustomSelectItem value={account} key={account.id} data-point={`Checking_Account_${account.id}`}>
                    <p>{account.number} - {account.nickname} (Available {account.availableBalance ? account.availableBalance.format() : account.availableCredit.format() })</p>
                </CustomSelectItem>
    });

    const dataPoint = props.dataPoint ?? "";

    return(
        <CustomSelect active={ isActive } value={ value } data-point={dataPoint} onChange={ props.onChange }>
            {accountItems}
        </CustomSelect>
    )
}

/**
 * @function getFieldsByType
 * @param {STRING} type - The type of custom fields needed, either "stopCheckCustomFields" or "stopRangeCustomFields".
 * @param {StopPayment} stopPayment - The instance of StopPayment in the page.
 * @param {Function} onCustomFieldChange - The instance of onCustomFieldChange in the state.
 * @param {OBJECT} ui - The instance of ui in the state.
 * @return {ARRAY} - Returns an array of custom fields deteremined by branding options.
 * @exported false
 * @description Returns an array of custom fields deteremined by branding options.
 */
function getFieldsByType(type, stopPayment, onCustomFieldChange, ui) {
  return stopPayment[type].reduce((acc, field) => {
    const fieldID = field.id;
    const fieldTypeFormatted = field.fieldType;
    const fieldType = fieldTypeFormatted.toLowerCase();
    const text = `${ fieldTypeFormatted }:`;

    if(fieldType.includes(`check number`)) {
      acc.push(
        <FormRowView key={ fieldID } text={ text }>
          <UITextInput data-point={ `Check_Number_Input` } onChange={ onCustomFieldChange(fieldID, type) }value={ field.enteredValue } />
        </FormRowView>
      );
    } else if(fieldType.includes(`payee`)) {
      acc.push(
        <FormRowView key={ fieldID } text={ text }>
          <UITextInput data-point={ `Payee_Input` } onChange={ onCustomFieldChange(fieldID, type) }value={ field.enteredValue } />
        </FormRowView>
      );
    } else if(fieldType.includes(`check date`)) {
      acc.push(
        <FormRowView key={ fieldID } text={ text } data-point="Transfers_Send_On">
            <DateSelect enablePast="true" enableFuture="true" data-point={ `Check_Date_Input` } onChange={ onCustomFieldChange(fieldID, type) } />
        </FormRowView>
      );
    } else if(fieldType.includes(`reason`)) {
      acc.push(
      <FormRowView key={ fieldID } text={ text }>
        <ReasonSelect active={ui.reasonDropdown.isActive} value={ field.enteredValue } onChange={ onCustomFieldChange(fieldID, type) } />
        {/* <CustomSelect active={ ui.reasonDropdown.isActive } value={ field.enteredValue } data-point={ `Reason_DD` } onChange={ onCustomFieldChange(fieldID, type) }>
          <CustomSelectItem data-point="Select_A_Reason">--Select a Reason--</CustomSelectItem>
          <CustomSelectItem data-point="Incorrect_Amount">Check Issued for Incorrect Amount</CustomSelectItem>
          <CustomSelectItem data-point="Lost_Check">Lost Check</CustomSelectItem>
          <CustomSelectItem data-point="Stale_Check">Stale Check</CustomSelectItem>
          <CustomSelectItem data-point="Stolen_Check">Stolen Check</CustomSelectItem>
          <CustomSelectItem data-point="Other">Other</CustomSelectItem>
        </CustomSelect> */}
      </FormRowView>
      );
    } else if(fieldType.includes(`check amount`)) {
      acc.push(
      <FormRowView key={ fieldID } text={ text }>
          <UIAmountInput data-point={ `Check_Amount_Input` } onChange={ onCustomFieldChange(fieldID, type) } value={ field.enteredValue ? field.enteredValue.amount : field.enteredValue } />
      </FormRowView>
      );
    }
    return acc;
  }, []);
}


export function ReasonSelect(props) {
  const { active, value, onChange } = props;
  const { branding } = useModel();
  const { options } = branding;
  const reasonOptions = options.stop_payment_reason_options_abi22.split(", ");
  
  const optionsList = reasonOptions.reduce(function (acc, option, key) {
    acc.push(<CustomSelectItem key={`Reason_Option_${key}`} data-point={`Reason_Option_${key}`}>{option}</CustomSelectItem>);
    return acc;
  }, [<CustomSelectItem key="Select_A_Reason" data-point="Select_A_Reason">--Select a Reason--</CustomSelectItem>]);

  return (
    <CustomSelect className="reason-select" active={active} value={value} data-point={`Reason_DD`} onChange={onChange}>
      {optionsList}
    </CustomSelect>
  )
}